import {getEnv} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {DefaultPrivileges} from '../api/common-contracts'
import type {ThemePreferences} from './color-modes'
import {fetchJSONIslandData, type JSONIslandData, type Runtime} from './json-island'

type InitialStateData = {
  themePreferences: JSONIslandData['theme-preferences']
  mediaUrls: JSONIslandData['media-urls'] | undefined
  loggedInUser: JSONIslandData['logged-in-user'] | undefined
  projectData: JSONIslandData['memex-data'] | undefined
  projectOwner: JSONIslandData['memex-owner'] | undefined
  projectCreator: JSONIslandData['memex-creator'] | undefined
  projectLimits: JSONIslandData['memex-limits']
  relayIds: JSONIslandData['memex-relay-ids'] | undefined
  viewerPrivileges: JSONIslandData['memex-viewer-privileges']
  feedbackLink: JSONIslandData['memex-feedback']['url']
  issueViewerFeedbackLink: JSONIslandData['memex-feedback']['issue_viewer_url']
  pwlBetaFeedbackLink: JSONIslandData['memex-feedback']['pwl_beta_url']
  isOrganization: boolean
  githubRuntime: Runtime
  githubDomain: string
  isDotcomRuntime: boolean
  isEnterpriseRuntime: boolean
  isMultiTenantEnterprise: boolean
  isBillingEnabled: boolean
  showTemplateDialog: boolean
  templateLinkedRepositoryId?: number
  copyProjectPartialUrl: JSONIslandData['copy-memex-project-partial-data']['url']
  createdWithTemplateMemex?: JSONIslandData['created-with-template-memex']
  systemTemplates?: JSONIslandData['memex-system-templates']
}

let initialState: InitialStateData | undefined = undefined

const defaultThemePreferences: ThemePreferences = {
  mode: undefined,
  light: undefined,
  dark: undefined,
  markdown_fixed_width_font: false,
  preferred_emoji_skin_tone: undefined,
}

/**
 * Lazily reads Initial State data from the JSON Island and caches it into a variable
 * so that we do not read from the JSON Island on subsequent calls
 * @returns A object containing several pieces of json island data. All of this data is
 * read-only and will not change for the life-cycle of the app
 */
export function getInitialState() {
  if (!initialState) {
    const projectOwner = fetchJSONIslandData('memex-owner')
    const projectData = fetchJSONIslandData('memex-data')
    const githubRuntime = fetchJSONIslandData('github-runtime') ?? 'dotcom'
    const isMultiTenantEnterprise = fetchJSONIslandData('is-github-multi-tenant-enterprise') ?? false
    const githubBillingEnabled = fetchJSONIslandData('github-billing-enabled') ?? false
    const feedback = fetchJSONIslandData('memex-feedback')
    const githubDomain: unknown = isFeatureEnabled('memex_client_env_github_domain')
      ? getEnv().githubDomain
      : fetchJSONIslandData('github-url')

    initialState = {
      themePreferences: fetchJSONIslandData('theme-preferences') ?? defaultThemePreferences,
      mediaUrls: fetchJSONIslandData('media-urls'),
      loggedInUser: fetchJSONIslandData('logged-in-user'),
      projectData,
      projectOwner,
      projectCreator: fetchJSONIslandData('memex-creator'),
      projectLimits: {
        projectItemLimit: 1200,
        singleSelectColumnOptionsLimit: 25,
        autoAddCreationLimit: 4,
        viewsLimit: 50,
        ...fetchJSONIslandData('memex-limits'),
      },
      relayIds: fetchJSONIslandData('memex-relay-ids'),
      viewerPrivileges: fetchJSONIslandData('memex-viewer-privileges') ?? DefaultPrivileges,
      feedbackLink: feedback?.url ?? 'https://github.com/github/feedback/discussions/categories/issues-feedback',
      issueViewerFeedbackLink:
        feedback?.issue_viewer_url ?? 'https://github.com/github/feedback/discussions/categories/issues-feedback',
      pwlBetaFeedbackLink: feedback?.pwl_beta_url ?? 'https://github.com/orgs/community/discussions/152407',
      isOrganization: projectOwner?.type === 'organization',
      githubRuntime,
      githubDomain: typeof githubDomain === 'string' ? githubDomain : '',
      isDotcomRuntime: githubRuntime === 'dotcom',
      isEnterpriseRuntime: githubRuntime === 'enterprise',
      isMultiTenantEnterprise,
      isBillingEnabled: githubBillingEnabled,
      showTemplateDialog: !!fetchJSONIslandData('memex-templates'),
      templateLinkedRepositoryId: fetchJSONIslandData('memex-template-linked-repository-id'),
      createdWithTemplateMemex: fetchJSONIslandData('created-with-template-memex'),
      copyProjectPartialUrl: fetchJSONIslandData('copy-memex-project-partial-data')?.url ?? '',
      systemTemplates: fetchJSONIslandData('memex-system-templates'),
    } as const
  }
  return initialState
}

/**
 * Test helper to force us to re-read this initial state
 * from the JSON Island after every test.
 *
 * Should only be called within tests
 */
export function resetInitialState() {
  initialState = undefined
}

/**
 * Determine if any more single-select column options can be added to a column.
 * @param options The current list of options, if any
 * @returns True if the user has reached the limit of single-select column options
 */
export function isOptionLimitReached<T>(options?: Array<T>): boolean {
  if (!options) return false
  return options.length >= getInitialState().projectLimits.singleSelectColumnOptionsLimit
}
